import { Badge } from "@/components/ui/baseUi/badge"
import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { useCoachOfferSidebar } from "@/modules/coachOffers/hooks/useCoachOfferSidebar"
import { BackgroundProfileIcon } from "@images/backgroundProfileIcon"
import { ArrowIcon } from "@images/svgIcons/arrowIcon"
import { useCoachCategories } from "@modules/coachCategories/hooks/useCoachCategories"
import { CoachOfferPricingOptionCard } from "@modules/coachOffers/components/coachOfferPricingOptionCard"
import type { CoachOfferEntity } from "@modules/coachOffers/entities/coachOfferEntity"
import { CoachOfferType } from "@modules/coachOffers/entities/coachOfferTypes"
import { useCoachOffer, useDeletePaymentOffer, useUpdatePaymentOffer } from "@modules/coachOffers/hooks/useCoachOffers"
import type { CoachOfferFormValues, CoachOfferPricingFormValues } from "@modules/coachOffers/types/coachOfferFormTypes"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useUser } from "@modules/users/hooks/useUser"
import dayjs from "dayjs"
import { CircleIcon, CirclePileIcon, LoaderCircle } from "lucide-react"
import { useMemo } from "react"

type CoachOfferResumeViewProps = {
  formValues?: CoachOfferFormValues
  offerId?: string
  onPrevious?: () => void
  onSave?: () => Promise<void>
  onEdit?: (offer: CoachOfferEntity) => void
  loading?: boolean
}

export function CoachOfferResumeView({
  formValues,
  offerId,
  onPrevious,
  onSave,
  onEdit,
  loading,
}: CoachOfferResumeViewProps) {
  const { data: coachOfferEntity } = useCoachOffer(offerId)
  const { data: categories } = useCoachCategories()
  const user = useUser()
  const language = useLanguage()
  const { navigateToCoachOfferPriceEdit } = useCoachOfferSidebar()

  const currency = user.stripeAccountCurrency ?? "EUR"
  const isFormMode = !!formValues
  const offer = formValues ?? coachOfferEntity

  const updatePaymentOffer = useUpdatePaymentOffer(offerId ?? "")
  const deletePaymentOffer = useDeletePaymentOffer(offerId ?? "")

  const paymentOffers: CoachOfferPricingFormValues[] = formValues
    ? formValues.paymentOffers
    : (coachOfferEntity?.paymentOffers ?? []).map((offer) => offer.fromEntityToFormValues())

  function handleEditPaymentOffer(paymentOfferId: string, currentValues: CoachOfferPricingFormValues) {
    navigateToCoachOfferPriceEdit(currentValues, (values) => {
      updatePaymentOffer.mutate({
        paymentOfferId,
        input: {
          price: values.price !== null ? { value: values.price, currency } : null,
          recurringPeriod: values.recurringPeriod,
          type: values.type,
          title: values.title,
          count: values.count,
          minimumCommitmentPeriod:
            values.minimumCommitmentPeriod && !isNaN(Number(values.minimumCommitmentPeriod))
              ? dayjs.duration({ months: Number(values.minimumCommitmentPeriod) }).toISOString()
              : null,
          commitmentPeriodAutoRenewal: values.commitmentPeriodAutoRenewal,
          validityPeriodMonths: values.validityPeriodMonths,
          displayOrder: values.displayOrder,
          renewable: values.renewable,
        },
      })
    })
  }

  const filteredCategories = useMemo(
    () => categories.filter((cat) => (formValues?.categoryIds ?? coachOfferEntity?.programs ?? []).includes(cat.id)),
    [categories, formValues, coachOfferEntity],
  )

  const urlImage = useMemo(() => {
    return offer?.image && "file" in offer.image && offer.image.file
      ? URL.createObjectURL(offer.image.file)
      : (offer?.image?.url ?? undefined)
  }, [offer])

  if (!offer?.name && !isFormMode) return null

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* SCROLLABLE CONTENT */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        {/* HEADER */}
        <div className="relative mb-5 flex h-65 rounded-b-2xl bg-surface-background-secondary">
          {/* Left: name + visibility */}
          <div className="z-1 m-auto flex w-[55%] flex-col items-center gap-1 px-4">
            {offer?.type && getTypeIcon(offer.type)}
            <Text fontStyle="HS" className="mt-2 text-center text-textOnSurface-subtle">
              {offer?.name}
            </Text>
          </div>

          {/* Right: cover image or decorative */}
          <div
            className="w-[45%] overflow-hidden"
            style={{ borderBottomRightRadius: "1rem", clipPath: "polygon(32% 0%, 100% 0%, 100% 100%, 0% 100%)" }}
          >
            {urlImage ? (
              <img src={urlImage} alt="" className="h-full w-full object-cover" />
            ) : (
              <BackgroundProfileIcon className="h-full bg-cover bg-no-repeat object-cover" color="currentColor" />
            )}
          </div>
        </div>

        {/* CONTENT */}
        <div className="flex flex-col gap-6 px-6 pb-4">
          {offer?.description && (
            <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces text-textOnSurface-subtle">
              {offer.description}
            </Text>
          )}

          {/* Session frequency */}
          {offer?.rules && offer.rules.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <Text fontStyle="LM" className="tracking-wide text-textOnSurface-subtle uppercase">
                {word("coach.offers.form.infos.restrictedPeriod.title")}
              </Text>
              <div className="grid grid-cols-3 gap-2.5">
                {offer.rules.map((rule, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-center rounded-[20px] border-2 border-surface-border-primary-default px-4 py-3"
                  >
                    <Text fontStyle="TS" className="text-center font-bold text-textOnSurface-default">
                      {`${rule.value.toString()} ${word("offer.form.rules.sessions", { count: rule.value })} ${word(`offer.form.rules.period.per.${rule.period}`)}`}
                    </Text>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pricing options */}
          {paymentOffers.length > 0 && (
            <div className="flex flex-col gap-3">
              {paymentOffers.map((pricingOffer, index) => (
                <CoachOfferPricingOptionCard
                  key={index}
                  offer={pricingOffer}
                  currency={currency}
                  locale={language}
                  hideActions={isFormMode}
                  onEdit={
                    !isFormMode
                      ? () => {
                          const entity = coachOfferEntity?.paymentOffers[index]
                          if (entity) handleEditPaymentOffer(entity.id, pricingOffer)
                        }
                      : undefined
                  }
                  onDelete={
                    !isFormMode
                      ? () => {
                          const entity = coachOfferEntity?.paymentOffers[index]
                          if (entity) deletePaymentOffer.mutate(entity.id)
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          )}

          {/* Categories */}
          {filteredCategories.length > 0 && (
            <div className="flex flex-col gap-2.5">
              <Text fontStyle="LM" className="tracking-wide text-textOnSurface-subtle uppercase">
                {word("coach.offers.form.infos.categories.title")}
              </Text>
              <div className="rounded-2xl border border-surface-border-gray-default px-5 py-4">
                <Text fontStyle="BS" className="text-textOnSurface-subtle">
                  {word("coach.offers.form.infos.categories.planningCategories.title")}
                </Text>
                <div className="mt-3 flex flex-wrap gap-2">
                  {filteredCategories.map((cat) => (
                    <Badge key={cat.id} variant="neutralSubtle" size="lg">
                      {cat.name}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* STICKY FOOTER */}
      <div className="sticky bottom-0 flex gap-5 border-t border-borderDefault bg-surface-background px-5 py-4">
        {isFormMode ? (
          <>
            <Button variant="outline" size="lg" onClick={onPrevious} className="flex-1 gap-2" disabled={loading}>
              <ArrowIcon arrowDirection="left" color="var(--textDefault-body)" width={14} height={14} />
              {word("global.previous")}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => void onSave?.()}
              className="flex-1"
              disabled={loading}
              isLoading={loading}
            >
              {word("global.save")}
              {loading && <LoaderCircle className="animate-spin" />}
            </Button>
          </>
        ) : (
          <Button
            variant="primary"
            size="lg"
            onClick={() => coachOfferEntity && onEdit?.(coachOfferEntity)}
            className="flex-1"
          >
            {word("global.edit")}
          </Button>
        )}
      </div>
    </div>
  )
}

function getTypeIcon(type: CoachOfferType) {
  switch (type) {
    case CoachOfferType.Individual:
      return <CircleIcon size={32} className="text-textOnSurface-subtle" />
    case CoachOfferType.Collective:
      return <CirclePileIcon size={32} className="text-textOnSurface-subtle" />
  }
}
